import "chartjs-adapter-date-fns";
import { ChartProps, Line } from "react-chartjs-2";
import { extLightThemeColors } from "@app/shared";
import { ChartData, TooltipItem } from "chart.js";
import { deepMerge } from "../../../helpers/deepMerge";
import { format } from "date-fns";
import { useMemo } from "react";

export type HardwareStatusChartProps<T> = {
    id?: string;
    title?: string;
    data: T[] | undefined;
    series: SeriesDetails<T>[];
    chartjsOverrides?: Partial<ChartProps<"line">>;
    isLoading?: boolean;
    yUnit?: string;
    domain?: [number, number];
} & ChartSizeProps;

export type ChartSizeProps = {
    width?: number;
    height?: number;
    margin?: { top: number; right: number; bottom: number; left: number };
};

export type SeriesAccessors<T> = {
    date: (data?: T) => Date | undefined;
    xAccessor: (data?: T) => Date | undefined;
    yAccessor: (data?: T) => number | undefined;
};

export type SeriesDetails<T> = {
    dataKey: string;
    accessors: SeriesAccessors<T>;
    opacity?: number;
};

export function HardwareStatusChart<T extends object>({ title, data, chartjsOverrides, series, isLoading, width, height = 330, yUnit }: HardwareStatusChartProps<T>) {
    const datasets: ChartData<"line">["datasets"] = [
        ...series.map((s, sIdx) => ({
            label: title,
            data: data?.map((d) => ({ x: s.accessors.xAccessor(d)! as any, y: s.accessors.yAccessor(d)! })) || [],
            borderColor: extLightThemeColors[sIdx % extLightThemeColors.length],
            backgroundColor: extLightThemeColors[sIdx % extLightThemeColors.length],
            pointRadius: 0,
        })),
    ];

    const timeUnit = useMemo((): "minute" | "hour" | "day" | "week" | "month" | undefined => {
        if (!data?.length || !series?.length) return undefined;
        const first = series[0].accessors.xAccessor(data[0]);
        const last = series[0].accessors.xAccessor(data[data.length - 1]);
        if (!first || !last) return undefined;
        const spanHours = Math.abs(new Date(last as any).getTime() - new Date(first as any).getTime()) / 3_600_000;
        if (spanHours <= 12) return "minute";
        if (spanHours <= 72) return "hour";
        if (spanHours <= 168) return "day";
        if (spanHours <= 744) return "day";
        return "week";
    }, [data, series]);

    const timeZoneInfo = useMemo(() => {
        if (!data?.length || !series?.length) {
            return { shortName: "", longName: "", offset: "" };
        }

        const rawDate = series[0].accessors.date(data[0]);
        // handle both Date objects and ISO string dates from SignalR
        const aDate = rawDate instanceof Date ? rawDate : typeof rawDate === "string" ? new Date(rawDate) : new Date();

        if (isNaN(aDate.getTime())) {
            return { shortName: "", longName: "", offset: "" };
        }

        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Get all timezone info at once
        const [shortFormatter, longFormatter, offsetFormatter] = ["short", "long", "longOffset"].map(
            (type) =>
                new Intl.DateTimeFormat("en-US", {
                    timeZone,
                    timeZoneName: type as any,
                })
        );

        const getTimeZonePart = (formatter: Intl.DateTimeFormat) => formatter.formatToParts(aDate).find((part: any) => part.type === "timeZoneName")?.value ?? "";

        return {
            shortName: getTimeZonePart(shortFormatter),
            longName: getTimeZonePart(longFormatter),
            offset: getTimeZonePart(offsetFormatter),
        };
    }, [data, series]);

    const chartjsProps = {
        data: {
            datasets,
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: title,
                    font: {
                        size: 20,
                        weight: "bold",
                    },
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: function (tooltipItem: TooltipItem<"line">) {
                            return `${series[tooltipItem.datasetIndex]?.dataKey?.padEnd(10, " ")} ${Math.round(tooltipItem.parsed.y)} ${yUnit}`; // Round & append '%'
                        },
                        title: function (tooltipItems: TooltipItem<"line">[]) {
                            //calculating here vs using timezoneInfo allows tooltip to be correct when periods span DST transitions
                            const date = new Date(tooltipItems[0].parsed.x);
                            const tzAbbr = new Intl.DateTimeFormat("en-US", {
                                timeZoneName: "short",
                            })
                                .formatToParts(date)
                                .find((part) => part.type === "timeZoneName")?.value;

                            return format(date, "PPpp") + " " + tzAbbr;
                        },
                    },
                },
            },
            animation: {
                duration: 500, // Smooth transition when data loads
            },
            interaction: {
                mode: "index", // Ensures all datasets for the same label are shown
                intersect: false, // Ensures tooltips show even if cursor isn't directly over a bar
            },
            scales: {
                y: {
                    beginAtZero: true,
                },
                x: {
                    type: "time", // Enables automatic time-based scaling
                    time: {
                        tooltipFormat: "PPpp    zzzz", // Formats tooltip using date-fns style
                        ...(timeUnit ? { unit: timeUnit } : {}),
                    },
                    ticks: {
                        source: "auto",
                        autoSkip: true, // Prevents cluttered ticks
                        maxTicksLimit: 10, // Adjust this as needed
                    },
                    title: {
                        display: true,
                        text: `${timeZoneInfo.longName} (${timeZoneInfo.offset})`,
                    },
                },
            },
        },
    } as Partial<ChartProps<"line">>;
    chartjsOverrides = chartjsOverrides ?? {};
    return (
        <>
            <div className="w-full p-4 overflow-hidden" style={{ height: height ? height + "px" : "330px" }}>
                <Line className="max-w-full" {...(deepMerge(chartjsProps, chartjsOverrides) as any)} width={width} height={height}></Line>
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
                        <p className="text-gray-500 text-lg">Loading...</p>
                    </div>
                )}
            </div>
        </>
    );
}
